/**
 * Zod Validation Schemas for Authentication Forms
 */

import { z } from 'zod';

/**
 * Email validation schema
 */
export const emailSchema = z
  .string()
  .email('Please enter a valid email address')
  .min(1, 'Email is required');

/**
 * Password validation schema
 * Minimum 8 characters with at least one uppercase letter
 */
export const passwordSchema = z
  .string()
  .min(1, 'Password is required')
  .min(8, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter');

/**
 * Email/Password Login Form Schema
 */
export const loginFormSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
});

export type LoginFormData = z.infer<typeof loginFormSchema>;

/**
 * Email/Password Signup Form Schema
 */
export const signupFormSchema = z
  .object({
    email: emailSchema,
    password: passwordSchema,
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export type SignupFormData = z.infer<typeof signupFormSchema>;

/**
 * Personal Information validation schemas
 */
export const personalInfoBaseSchema = z.object({
  fullName: z
    .string()
    .min(1, 'Full name is required')
    .max(100, 'Full name must be at most 100 characters'),
  phone: z
    .string()
    .min(1, 'Phone number is required'),
  nationality: z
    .string()
    .min(1, 'Nationality is required')
});

/**
 * Tutor Profile Schema (all fields required)
 */
export const tutorProfileSchema = personalInfoBaseSchema.extend({
  dateOfBirth: z
    .string()
    .min(1, 'Date of birth is required')
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format'),
  gender: z.enum(['male', 'female', 'other']),
});

/**
 * Parent Profile Schema (all fields required, including gender)
 */
export const parentProfileSchema = personalInfoBaseSchema.extend({
  gender: z.enum(['male', 'female', 'other'], {
    message: 'Gender is required'
  }),
});

export type TutorProfileData = z.infer<typeof tutorProfileSchema>;
export type ParentProfileData = z.infer<typeof parentProfileSchema>;
